// Centralized data cache system to reduce Firestore reads
// This will cache all commonly accessed data to minimize Firebase usage

import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

class DataCache {
  constructor() {
    this.cache = {
      dancers: null,
      competitions: null,
      disciplines: null,
      studios: null,
      lastUpdated: null
    };
    this.isLoading = false;
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  }

  // Check if cache is still valid
  isCacheValid() {
    if (!this.cache.lastUpdated) return false;
    return Date.now() - this.cache.lastUpdated < this.CACHE_DURATION;
  }

  // Get all dancer names (most frequently accessed)
  async getAllDancerNames() {
    if (this.isCacheValid() && this.cache.dancers) {
      console.log('✅ Using cached dancer names');
      return this.cache.dancers;
    }

    if (this.isLoading) {
      // Wait for existing load to complete
      await this.waitForLoad();
      return this.cache.dancers || [];
    }

    return await this.loadAllData().then(() => this.cache.dancers || []);
  }

  // Get competition data
  async getCompetitions() {
    if (this.isCacheValid() && this.cache.competitions) {
      console.log('✅ Using cached competitions');
      return this.cache.competitions;
    }

    if (this.isLoading) {
      await this.waitForLoad();
      return this.cache.competitions || [];
    }

    return await this.loadAllData().then(() => this.cache.competitions || []);
  }

  // Get discipline structure
  async getDisciplines() {
    if (this.isCacheValid() && this.cache.disciplines) {
      console.log('✅ Using cached disciplines');
      return this.cache.disciplines;
    }

    if (this.isLoading) {
      await this.waitForLoad();
      return this.cache.disciplines || [];
    }

    return await this.loadAllData().then(() => this.cache.disciplines || []);
  }

  // Load all data in a single optimized fetch
  async loadAllData() {
    if (this.isLoading) {
      return await this.waitForLoad();
    }

    this.isLoading = true;
    console.log('🔄 Loading all data into cache...');

    try {
      const startTime = Date.now();
      const dancerNames = new Set();
      const competitions = [];
      const disciplines = new Map();

      // First, load old structure competitions
      console.log('📁 Loading old structure competitions...');
      const oldCompetitionsRef = collection(db, 'competitions');
      const oldCompetitionsSnap = await getDocs(oldCompetitionsRef);

      oldCompetitionsSnap.forEach(doc => {
        const competition = { id: doc.id, ...doc.data() };
        competitions.push(competition);

        // Extract dancer names
        if (competition.results && Array.isArray(competition.results)) {
          competition.results.forEach(result => {
            const name = result.dancerName || result.name;
            if (name && name.trim()) {
              dancerNames.add(name.trim());
            }
          });
        }
      });

      // Load new structure
      console.log('🏗️ Loading new structure disciplines...');
      const disciplinesRef = collection(db, 'disciplines');
      const disciplinesSnap = await getDocs(disciplinesRef);

      for (const disciplineDoc of disciplinesSnap.docs) {
        const disciplineName = disciplineDoc.id;
        const disciplineData = {
          name: disciplineName,
          categories: new Map()
        };

        const categoriesRef = collection(disciplineDoc.ref, 'categories');
        const categoriesSnap = await getDocs(categoriesRef);

        for (const categoryDoc of categoriesSnap.docs) {
          const categoryName = categoryDoc.id;
          const categoryData = {
            name: categoryName,
            competitions: [],
            dances: new Map()
          };

          // Load category competitions
          const competitionsRef = collection(categoryDoc.ref, 'competitions');
          const competitionsSnap = await getDocs(competitionsRef);

          competitionsSnap.forEach(compDoc => {
            const competition = { 
              id: compDoc.id, 
              ...compDoc.data(),
              discipline: disciplineName,
              category: categoryName
            };
            categoryData.competitions.push(competition);
            competitions.push(competition);

            // Extract dancer names
            if (competition.results && Array.isArray(competition.results)) {
              competition.results.forEach(result => {
                const name = result.dancerName || result.name;
                if (name && name.trim()) {
                  dancerNames.add(name.trim());
                }
              });
            }
          });

          // Load dances for Latin
          if (disciplineName.toLowerCase() === 'latin') {
            const dancesRef = collection(categoryDoc.ref, 'dances');
            const dancesSnap = await getDocs(dancesRef);

            for (const danceDoc of dancesSnap.docs) {
              const danceName = danceDoc.id;
              const danceData = {
                name: danceName,
                competitions: []
              };

              const danceCompetitionsRef = collection(danceDoc.ref, 'competitions');
              const danceCompetitionsSnap = await getDocs(danceCompetitionsRef);

              danceCompetitionsSnap.forEach(compDoc => {
                const competition = { 
                  id: compDoc.id, 
                  ...compDoc.data(),
                  discipline: disciplineName,
                  category: categoryName,
                  dance: danceName
                };
                danceData.competitions.push(competition);
                competitions.push(competition);

                // Extract dancer names
                if (competition.results && Array.isArray(competition.results)) {
                  competition.results.forEach(result => {
                    const name = result.dancerName || result.name;
                    if (name && name.trim()) {
                      dancerNames.add(name.trim());
                    }
                  });
                }
              });

              categoryData.dances.set(danceName, danceData);
            }
          }

          disciplineData.categories.set(categoryName, categoryData);
        }

        disciplines.set(disciplineName, disciplineData);
      }

      // Convert sets/maps to arrays for easier use
      const sortedDancerNames = Array.from(dancerNames).sort();
      const disciplinesArray = Array.from(disciplines.entries()).map(([name, data]) => ({
        name,
        categories: Array.from(data.categories.entries()).map(([catName, catData]) => ({
          name: catName,
          competitions: catData.competitions,
          dances: catData.dances.size > 0 ? Array.from(catData.dances.entries()).map(([danceName, danceData]) => ({
            name: danceName,
            competitions: danceData.competitions
          })) : []
        }))
      }));

      // Store in cache
      this.cache = {
        dancers: sortedDancerNames,
        competitions: competitions,
        disciplines: disciplinesArray,
        studios: [], // We can extend this later
        lastUpdated: Date.now()
      };

      const loadTime = Date.now() - startTime;
      console.log(`✅ Cache loaded successfully in ${loadTime}ms`);
      console.log(`📊 Cache contains:`);
      console.log(`   - ${sortedDancerNames.length} dancers`);
      console.log(`   - ${competitions.length} competitions`);
      console.log(`   - ${disciplinesArray.length} disciplines`);

    } catch (error) {
      console.error('❌ Error loading data into cache:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  // Wait for ongoing load to complete
  async waitForLoad() {
    while (this.isLoading) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // Clear cache to force reload
  clearCache() {
    console.log('🗑️ Clearing data cache');
    this.cache = {
      dancers: null,
      competitions: null,
      disciplines: null,
      studios: null,
      lastUpdated: null
    };
  }

  // Force refresh cache immediately
  async forceRefresh() {
    console.log('🔄 Force refreshing cache...');
    this.clearCache();
    return await this.loadAllData();
  }

  // Get cache status
  getCacheStatus() {
    return {
      isValid: this.isCacheValid(),
      lastUpdated: this.cache.lastUpdated,
      dancerCount: this.cache.dancers?.length || 0,
      competitionCount: this.cache.competitions?.length || 0,
      disciplineCount: this.cache.disciplines?.length || 0,
      isLoading: this.isLoading
    };
  }

  // Search dancers by name (cached search)
  searchDancers(searchTerm) {
    if (!this.cache.dancers) return [];
    
    const term = searchTerm.toLowerCase().trim();
    if (!term) return [];

    return this.cache.dancers.filter(name => 
      name.toLowerCase().includes(term)
    ).slice(0, 20); // Limit to 20 results
  }

  // Get competitions for a specific dancer (cached search)
  getDancerCompetitions(dancerName) {
    if (!this.cache.competitions) return [];

    return this.cache.competitions.filter(comp => 
      comp.results && comp.results.some(result => {
        const name = result.dancerName || result.name;
        return name && name.trim().toLowerCase() === dancerName.toLowerCase();
      })
    );
  }
}

// Create singleton instance
const dataCache = new DataCache();

export default dataCache;

// Convenience exports
export const getAllDancerNames = () => dataCache.getAllDancerNames();
export const getCompetitions = () => dataCache.getCompetitions();
export const getDisciplines = () => dataCache.getDisciplines();
export const searchDancers = (term) => dataCache.searchDancers(term);
export const getDancerCompetitions = (name) => dataCache.getDancerCompetitions(name);
export const clearDataCache = () => dataCache.clearCache();
export const forceRefreshCache = () => dataCache.forceRefresh();
export const getDataCacheStatus = () => dataCache.getCacheStatus();
